import { useAuth } from '../../lib/auth/useAuth';
import { useAccount } from '../../lib/account/useAccount';
import { ACCOUNT_ROLE_LABELS } from '../../lib/account/role-labels';
import { inlineErrorMessage } from '../../lib/error-handling';
import { useSwitchAccount } from '../../hooks/useSwitchAccount';
import { useUserAccounts } from '../../hooks/useUserAccounts';
import { Button } from '../ui/Button';
import { ReadError } from '../ui/ReadError';
import { SpinnerIcon } from '../ui/SpinnerIcon';

const rowClass =
  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-secondary transition-colors hover:bg-bg-muted focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-60';

/**
 * The user's accounts inside the profile menu, for moving between them without
 * signing out. Mount it only while the menu is open: the list is fetched on
 * mount, so a closed menu costs no request.
 */
export function AccountSwitcher({ onClose }: { onClose: () => void }) {
  const { accountId } = useAuth();
  const { account } = useAccount();
  const { accounts, isLoading, isError, isFetchNextPageError, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } =
    useUserAccounts();
  // The failure is shown inline in the menu, so the global toast is suppressed
  // by supplying a handler.
  const switchAccount = useSwitchAccount({ onError: () => undefined });

  // An administered entity has no membership row, so its firm is the account
  // the user belongs to.
  const administeredBy = account?.administeredBy ?? null;
  const currentAccountId = administeredBy?.accountId ?? accountId;

  if (isLoading) return null;
  if (isError && accounts.length === 0) {
    return (
      <div className="border-b border-border px-4 py-3">
        <ReadError message="Couldn't load your accounts" onRetry={() => void refetch()} className="text-xs" />
      </div>
    );
  }
  // Inside an administered entity the firm is worth offering even when it is the only account.
  if (accounts.length <= 1 && !administeredBy) return null;

  function choose(targetId: string) {
    if (targetId === currentAccountId && !administeredBy) {
      onClose();
      return;
    }
    switchAccount.mutate({ accountId: targetId, to: '/dashboard' });
  }

  return (
    <div className="border-b border-border p-1.5">
      <p className="px-3 pb-1 pt-1.5 text-xs font-medium text-text-muted">Switch account</p>
      <ul className="max-h-48 overflow-y-auto">
        {accounts.map((userAccount) => {
          const isCurrent = userAccount.account.id === currentAccountId;
          const isTarget = switchAccount.isPending && switchAccount.variables?.accountId === userAccount.account.id;
          return (
            <li key={userAccount.account.id}>
              <button
                type="button"
                className={rowClass}
                aria-current={isCurrent ? 'true' : undefined}
                disabled={switchAccount.isPending}
                onClick={() => choose(userAccount.account.id)}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center text-primary">
                  {isTarget ? (
                    <SpinnerIcon className="h-4 w-4" />
                  ) : (
                    isCurrent && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                        <path d="m5 12 5 5L20 7" />
                      </svg>
                    )
                  )}
                </span>
                <span className="min-w-0 flex-1 truncate">{userAccount.account.name}</span>
                <span className="shrink-0 text-xs text-text-muted">{ACCOUNT_ROLE_LABELS[userAccount.role]}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {hasNextPage && (
        <div className="flex flex-col items-center px-3 py-1.5">
          {isFetchNextPageError && (
            <p className="pb-1.5 text-xs text-text-muted" role="alert">
              Couldn't load more accounts.
            </p>
          )}
          <Button variant="secondary" size="sm" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
            Load more
          </Button>
        </div>
      )}
      {switchAccount.isError && (
        <p className="px-3 pb-1.5 pt-1 text-xs text-error" role="alert">
          {inlineErrorMessage(switchAccount.error, "Couldn't switch account.")}
        </p>
      )}
    </div>
  );
}
